import React, { useEffect, useMemo, useState } from "react";
import { Alert, alpha, Box, Chip, Stack, Typography } from "@mui/material";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import { useFormik } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  useAssignClassesUpdateMutation,
  useGetTeamQuery,
  useGetallLocationQuery,
  useGetallSubjectQuery,
  useGetStaffGradesAssignStaffGetPerentQuery,
  useGetAssignClassListFilterQuery,
  useGetAssignClassGetAllCalederQuery,
  useGetOccurrenceEditFormQuery,
} from "../../../Api/attoDeskApi";
import {
  CustomerOption,
  IClassCreateList,
  IClassEdit,
  IStudentLocation,
  ITeam,
  IGrade,
  ISubject,
  IAssignClassListDetails,
} from "../../../Api/Interface/api.interface";
import { ClassType, TeachMood } from "../../../Core/Enum/enum";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import { useConfirm } from "../../../Components/Confirm";
import BackDrop from "../../../Components/Loader/BackDrop";
import ClassForm, {
  normalizeTeachMode,
  requiresLocation,
  TimeConflict,
} from "../ClassForm";
import { normalizeWeekday, deriveWeekdaysFromClassData } from "../weekdayHelpers";
import { usePermission } from "../../../hooks/usePermission";

/**
 * Edit Class page.
 *
 * Uses the SAME ClassForm component as Create Class (see ../ClassForm/) so
 * the visual language, spacing, controls and responsive behaviour are
 * guaranteed to match. Page-specific concerns live here:
 *   - Prefill: initial formik values + selected subjects/days from the class
 *   - Safety rules: capacity floor, protected subjects, mode narrowing
 *   - Edit scope: this / future / series (compact band rendered by ClassForm)
 *   - Return URL: preserved through Cancel + post-save
 *
 * The old modal-dialog Edit ("NewEditClassPopUp") is superseded by this
 * page; callers navigate to `/classes/:id/edit?return_url=…` instead of
 * opening a dialog.
 */

/**
 * Serialise every editable class value to a single canonical string so the
 * server snapshot and the live form snapshot can be compared with `===` (and
 * cheaply cached by useMemo). Normalisation choices:
 *
 *   - numbers/ids: coerced with Number(); NaN/undefined/'' → null.
 *   - strings: trimmed; '' → null.
 *   - arrays (subjects, weekdays): coerced element-wise, dedup'd, sorted so
 *     order-only differences don't count as a change.
 *   - dates: rendered as YYYY-MM-DD via dayjs so a Dayjs object and its
 *     string equivalent compare equal.
 *   - tax status: '' / null → 'exempt' (the initialValues default).
 *   - program type: only 'special' or 'regular' are meaningful; anything else
 *     falls back to 'regular' (the initialValues default).
 *   - default_fee_effective_from: '' / null → today, matching the fallback
 *     applied in both initialValues and onSubmit so an untouched form does
 *     not report a fake change on the very first render.
 *
 * Returning a single string means the two memos are stable across renders
 * that don't change any tracked field, which keeps hasChanges cheap.
 */
const buildEditSnapshot = (input: {
  staff_id: unknown;
  location_id: unknown;
  class_mode: unknown;
  class_type: unknown;
  number_of_student: unknown;
  notes: unknown;
  startTime: unknown;
  endTime: unknown;
  period_from: unknown;
  period_to: unknown;
  default_fee: unknown;
  default_tax_status: unknown;
  default_fee_effective_from: unknown;
  program_type: unknown;
  subjects: unknown;
  weekdays: unknown;
}): string => {
  const numOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const strOrNull = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
  };
  const dateOrNull = (v: unknown): string | null => {
    if (v === null || v === undefined || v === "") return null;
    const d = dayjs(v as any);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  };
  const sortedNums = (v: unknown): number[] => {
    if (!Array.isArray(v)) return [];
    const out: number[] = [];
    for (const x of v) {
      const n = Number(x);
      if (Number.isFinite(n)) out.push(n);
    }
    return Array.from(new Set(out)).sort((a, b) => a - b);
  };
  const sortedStrs = (v: unknown): string[] => {
    if (!Array.isArray(v)) return [];
    const out: string[] = [];
    for (const x of v) {
      const s = strOrNull(x);
      if (s !== null) out.push(s);
    }
    return Array.from(new Set(out)).sort();
  };

  const today = dayjs().format("YYYY-MM-DD");
  const taxRaw = strOrNull(input.default_tax_status);
  const programRaw = strOrNull(input.program_type);

  // class_mode and weekdays go through case/alias normalisation so a
  // server value of "online" and a UI value of "Online" (or "in_person"
  // vs "Offline") compare equal. Without this, flipping Teaching Mode on
  // a legacy-cased row left hasChanges=false and silently disabled Save
  // Changes — see Edit Class audit §1 (2026-10-08). Weekdays canonicalise
  // via normalizeWeekday so "Tuesday" / "tuesday" / "Tue" all resolve to
  // "Tuesday" — otherwise a mode-only change could spuriously look dirty
  // on weekdays (false positive) OR validation could fire for a schedule
  // that actually exists (false negative, the original bug).
  const modeRaw = normalizeTeachMode(input.class_mode);
  const weekdaysNorm = Array.isArray(input.weekdays)
    ? input.weekdays
        .map((d) => normalizeWeekday(d))
        .filter((d): d is string => !!d)
    : [];
  const canonical = {
    staff_id: numOrNull(input.staff_id),
    location_id: numOrNull(input.location_id),
    class_mode: modeRaw === "" ? null : modeRaw,
    class_type: strOrNull(input.class_type),
    number_of_student: numOrNull(input.number_of_student),
    notes: strOrNull(input.notes),
    startTime: strOrNull(input.startTime),
    endTime: strOrNull(input.endTime),
    period_from: dateOrNull(input.period_from),
    period_to: dateOrNull(input.period_to),
    default_fee: numOrNull(input.default_fee),
    default_tax_status: taxRaw ?? "exempt",
    default_fee_effective_from: dateOrNull(input.default_fee_effective_from) ?? today,
    program_type: programRaw === "special" ? "special" : "regular",
    subjects: sortedNums(input.subjects),
    weekdays: Array.from(new Set(weekdaysNorm.filter((d) => d !== ""))).sort(),
  };

  return JSON.stringify(canonical);
};

// `deriveWeekdaysFromClassData` + `normalizeWeekday` live in
// `../weekdayHelpers` so they can be unit-tested without pulling React,
// MUI, or RTK Query. See `../weekdayHelpers.test.ts`.

const parseStaffLocationIds = (raw: any): number[] => {
  if (raw === null || raw === undefined || raw === "") return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(Number).filter(Boolean);
    if (typeof parsed === "number") return [parsed];
  } catch {
    const num = Number(raw);
    if (!isNaN(num) && num > 0) return [num];
  }
  return [];
};

const Index: React.FC = () => {
  const { id: routeId = "" } = useParams<{ id: string }>();
  const classId = Number(routeId);
  const navigate = useNavigate();
  const location = useLocation();
  const { showErrorMessage, showMessage } = useNotifier();

  // Preserve the originating list's URL for Cancel + post-save. Only same-app
  // paths accepted so a crafted return_url cannot open external sites.
  const returnUrl = useMemo(() => {
    const qp = new URLSearchParams(location.search).get("return_url");
    return qp && qp.startsWith("/") ? qp : `/classes/${routeId}`;
  }, [location.search, routeId]);

  // Occurrence context (only present when we arrived from a class-sessions
  // link). Used to render the edit-mode context band ("Selected occurrence:
  // {date}") and to compute the effective date for scoped edits.
  const occurrenceId = useMemo(
    () => new URLSearchParams(location.search).get("occurrence_id") || "",
    [location.search]
  );
  const { data: occData } = useGetOccurrenceEditFormQuery(occurrenceId, {
    skip: !occurrenceId,
  });
  const occurrence = (occData as any)?.data ?? null;

  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(currentSearchQuery, 300);

  // ── Class data (source of prefill) ──
  // Reuses the same filter query CheckStaff uses; passing the class id resolves
  // to a single-item response we consume as the record.
  const {
    data: classFilterData,
    isLoading: classIsLoading,
  } = useGetAssignClassListFilterQuery({
    id: String(classId),
    staff_id: "",
    location_id: "",
    class_mode: "",
    class_type: "",
    select_grade_subject_ids: [],
    selected_days: [],
  });

  const classData = useMemo<IClassCreateList | null>(() => {
    const rows = (classFilterData as any)?.data;
    if (Array.isArray(rows) && rows.length > 0) return rows[0] as IClassCreateList;
    return null;
  }, [classFilterData]);

  // ── Staff / location / subject lookups (same hooks Create uses) ──
  const { data: locationData, isLoading: locationIsLoading } = useGetallLocationQuery({ searchText: "" });
  const {
    data: studentPerentData,
    isLoading: studentPerentIsLoading,
    refetch,
  } = useGetTeamQuery({ searchText: currentSearchQuery, id: "" });
  const {
    data: subjectData,
    isLoading: subjectIsLoading,
    refetch: subjectRefetch,
  } = useGetallSubjectQuery({ searchText: "" });

  const staffIdForSubjects = String(classData?.staff_id ?? "");
  const {
    data: subjectGradeData,
    isLoading: subjectGradeIsLoading,
    isError,
    refetch: subjectGradeRefetch,
  } = useGetStaffGradesAssignStaffGetPerentQuery(
    { id: staffIdForSubjects },
    { skip: !staffIdForSubjects }
  );

  // Existing staff schedule (for conflict banner) — same fetch as Create.
  const { data: staffScheduleData } = useGetAssignClassGetAllCalederQuery(
    {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().add(2, "month").endOf("month").format("YYYY-MM-DD"),
      empid: staffIdForSubjects,
    },
    { skip: !staffIdForSubjects }
  );

  const staffExistingClasses = useMemo((): IAssignClassListDetails[] => {
    if (!staffScheduleData?.data || !Array.isArray(staffScheduleData.data)) return [];
    // Exclude the class being edited from conflict detection — its own
    // schedule shouldn't count as a conflict with itself.
    return (staffScheduleData.data as IAssignClassListDetails[]).filter(
      (c) => c.schedule?.class_id !== classId
    );
  }, [staffScheduleData, classId]);

  // ── Derived option lists ──
  const studentPerentList = useMemo(() => (studentPerentData?.data ?? []) as ITeam[], [studentPerentData?.data]);
  const locationList = useMemo(() => (locationData?.data ?? []) as IStudentLocation[], [locationData?.data]);
  const subjectDataList: ISubject[] = Array.isArray(subjectData?.data) ? (subjectData?.data as ISubject[]) : [];
  const subjectGRadeDataList: IGrade[] = Array.isArray(subjectGradeData?.data) ? (subjectGradeData?.data as IGrade[]) : [];

  const subjectMap = useMemo(() => {
    const map = new Map<number, string>();
    subjectDataList.forEach((s) => map.set(s.id, s.subject_name));
    return map;
  }, [subjectDataList]);

  const studentPerentOptions = useMemo<CustomerOption[]>(() => {
    if (!Array.isArray(studentPerentList)) return [];
    return studentPerentList.map((cust) => ({
      label: `${cust.teamFirstName || ""} ${cust.middle_name || ""} ${cust.teamLastName || ""}`.trim(),
      value: cust.id,
    }));
  }, [studentPerentList]);

  const locationOptions = useMemo<CustomerOption[]>(() => {
    return locationList.map((loc) => ({
      label: `${loc.location_name || loc.location_address}`,
      value: loc.id,
    }));
  }, [locationList]);

  // Debounced staff search (matches Create's behaviour).
  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // ── Selected sets (kept outside formik, mirroring Create) ──
  const [selectedSubject, setSelectedSubject] = useState<number[]>([]);
  usePermission();

  // ClassForm's "Add Teaching Subject" dialog returns newly-selected
  // grade+subject pair-ids; append them to the local set. Protected pairs
  // + existing selections are untouched — we only add IDs, never remove.
  const handleAddSubjectAssignments = (newIds: number[]) => {
    if (!newIds || newIds.length === 0) return;
    setSelectedSubject((prev) => {
      const set = new Set(prev);
      newIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };
  const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>([]);
  const [programType, setProgramType] = useState<"regular" | "special">("regular");
  // Default to "This and Future Sessions" per spec — schedule edits always
  // affect future occurrences by design; the user can still narrow/expand.
  const [editScope, setEditScope] = useState<"this" | "future" | "series">("future");

  // Seed selected subjects / days / program-type from the fetched class once.
  useEffect(() => {
    if (!classData) return;
    if (Array.isArray(classData.select_grade_subject_ids)) {
      setSelectedSubject(classData.select_grade_subject_ids as number[]);
    }
    setSelectedWeekDays(deriveWeekdaysFromClassData(classData));
    const pt = (classData as any).program_type;
    if (pt === "special" || pt === "regular") setProgramType(pt);
  }, [classData]);

  // ── Safety rule inputs ──
  const assignedCount = (classData as any)?.assignStudent?.length ?? 0;
  // Protected subjects are ONLY those the backend explicitly marks as
  // protected. The old fallback ("any subject on a class with any
  // assigned student is protected") was blocking legitimate
  // effective-dated schedule edits — historical class_occurrences carry
  // grade/subject snapshots (grade_id, subject_id, *_name_snapshot on
  // the occurrence row) so past sessions remain immutable regardless of
  // what the class master now says. Removing a subject only affects
  // FUTURE occurrence generation and picker options; per-student
  // assign_student_classes references stay verbatim. The save endpoint
  // returns `warnings` when a removed subject was referenced by active
  // student assignments so the user is informed without being blocked.
  const protectedSubjectIds = useMemo(() => {
    const api = (classData as any)?.protected_subject_ids;
    return Array.isArray(api) ? new Set<number>(api.map(Number)) : new Set<number>();
  }, [classData]);

  // ── Formik ──
  const [updateClass, { isLoading: isUpdating }] = useAssignClassesUpdateMutation();

  const formik = useFormik<IClassEdit>({
    enableReinitialize: true,
    initialValues: {
      id: classId,
      staff_id: (classData?.staff_id as any) ?? null,
      location_id: (classData?.location_id as any) ?? null,
      class_mode: normalizeTeachMode(classData?.class_mode),
      class_type: classData?.class_type ?? "",
      number_of_student: classData?.number_of_student ?? null,
      select_grade_subject_ids: [],
      period_based_from_date: (classData as any)?.period_based_from_date
        ? dayjs((classData as any).period_based_from_date)
        : null,
      period_based_to_date: (classData as any)?.period_based_to_date
        ? dayjs((classData as any).period_based_to_date)
        : null,
      notes: (classData as any)?.notes ?? "",
      selected_days: [],
      // The class-filter endpoint returns snake_case start_time/end_time (see
      // classesAssignController@getAssignClassListFilter). Older list endpoints
      // that share the IClassCreateList type use camelCase startTime/endTime,
      // so read both. Missing either kept the whole form invalid on load and
      // permanently disabled Save Changes.
      startTime: (classData as any)?.start_time ?? classData?.startTime ?? "",
      endTime: (classData as any)?.end_time ?? classData?.endTime ?? "",
      default_fee: (classData as any)?.default_fee != null ? Number((classData as any).default_fee) : null,
      default_tax_status: ((classData as any)?.default_tax_status as "exempt" | "applicable") ?? "exempt",
      default_fee_effective_from: (classData as any)?.default_fee_effective_from ?? dayjs().format("YYYY-MM-DD"),
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      const isValidTime = (t: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(t);
      const toMinutes = (t: string) => {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
      };
      if (!values.startTime || !isValidTime(values.startTime)) errors.startTime = "Start time is required (HH:mm)";
      if (!values.endTime || !isValidTime(values.endTime)) errors.endTime = "End time is required (HH:mm)";
      if (
        values.startTime &&
        values.endTime &&
        isValidTime(values.startTime) &&
        isValidTime(values.endTime) &&
        toMinutes(values.endTime) <= toMinutes(values.startTime)
      ) {
        errors.endTime = "End time must be after start time";
      }
      // Capacity floor — a class's capacity cannot drop below the currently
      // assigned students. Server also enforces this; blocking here avoids a
      // round-trip and surfaces the actionable message inline.
      if (
        assignedCount > 0 &&
        values.number_of_student != null &&
        Number(values.number_of_student) < assignedCount
      ) {
        errors.number_of_student = `Capacity cannot be lower than the ${assignedCount} student(s) currently assigned.`;
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          id: values.id,
          staff_id: values.staff_id,
          location_id: values.location_id,
          // Send the canonical UI token (Online/Offline/Hybrid) that the
          // backend's StudyMode::normalize already understands. Guards
          // against a legacy-cased value re-saving as-is and perpetuating
          // the mismatch that caused the Save-stays-disabled bug.
          class_mode: normalizeTeachMode(values.class_mode),
          class_type: values.class_type,
          program_type: programType,
          number_of_student: values.number_of_student,
          select_grade_subject_ids: selectedSubject,
          notes: values.notes,
          // Send canonical capitalised full names ("Monday") so the backend
          // write path and any downstream `assign_classes_schedules.day`
          // queries don't accumulate mixed-case rows. normalizeWeekday
          // filters out anything unresolvable rather than passing junk.
          selected_days: selectedWeekDays
            .map((d) => normalizeWeekday(d))
            .filter((d): d is string => !!d),
          period_based_from_date: values.period_based_from_date
            ? values.period_based_from_date.format("YYYY-MM-DD")
            : null,
          period_based_to_date: values.period_based_to_date
            ? values.period_based_to_date.format("YYYY-MM-DD")
            : null,
          startTime: values.startTime,
          endTime: values.endTime,
          // Edit scope only meaningful for recurring classes; server ignores
          // when not applicable. Defaults to "future" per spec (schedule edits
          // affect future occurrences from the effective date).
          edit_scope: editScope,
          // Effective date + originating occurrence (when present) so the
          // backend can regenerate future occurrences from the right anchor.
          effective_date: occurrence?.scheduledDate || dayjs().format("YYYY-MM-DD"),
          occurrence_id: occurrenceId || null,
          // Billing v2 fee edits — only send when the admin actually
          // filled the field in; a null default_fee means "don't touch
          // the current fee history".
          default_fee: values.default_fee ?? null,
          default_tax_status: values.default_tax_status ?? "exempt",
          default_fee_effective_from: values.default_fee_effective_from ?? dayjs().format("YYYY-MM-DD"),
        };
        const response: any = await updateClass(payload).unwrap();
        if (!response?.status) {
          showErrorMessage(response?.message || "Update failed");
          return;
        }
        showMessage(response?.message || "Class updated successfully");
        // Warnings surface non-blocking notices from the save:
        //   - removed_subject_referenced_by_students: a subject was removed
        //     but still referenced by active per-student assignments;
        //     assignments preserved.
        //   - import_source_promoted: a legacy-imported class was taken over
        //     by the app's standard schedule engine on this edit (one-way).
        const warnings: any[] = Array.isArray(response?.data?.warnings) ? response.data.warnings : [];
        for (const w of warnings) {
          if (w?.kind === "removed_subject_referenced_by_students") {
            const label = Array.isArray(w.removed) ? w.removed.join(", ") : "";
            const n = Number(w.student_count) || 0;
            showErrorMessage(
              `Note: ${n} student ${n === 1 ? "assignment" : "assignments"} still reference removed subject${label ? " (" + label + ")" : ""}. Past sessions and records are preserved; adjust individual assignments if needed.`
            );
          } else if (w?.kind === "import_source_promoted") {
            showErrorMessage(
              "Note: this class was imported from a legacy backup and has now been taken over by the app's schedule engine. Past sessions are preserved. Going forward, every enrolled student is scheduled on every configured weekday — adjust individual assignments if the legacy per-day roster shouldn't carry across."
            );
          }
        }
        navigate(returnUrl);
      } catch (e: any) {
        showErrorMessage(e?.data?.message || e?.message || "Something went wrong");
      }
    },
  });

  // ── Location-derived staff/location filtering (same rules as Create) ──
  const staffLocationIds = useMemo((): number[] => {
    if (!formik.values.staff_id) return [];
    const staff = studentPerentList.find((s) => s.id === Number(formik.values.staff_id));
    return parseStaffLocationIds(staff?.location_id);
  }, [formik.values.staff_id, studentPerentList]);

  const filteredLocationOptions = useMemo(() => {
    if (staffLocationIds.length === 0) return locationOptions;
    return locationOptions.filter((opt) => staffLocationIds.includes(opt.value));
  }, [locationOptions, staffLocationIds]);

  const filteredStaffOptions = useMemo(() => {
    const locId = Number(formik.values.location_id);
    if (!locId) return studentPerentOptions;
    return studentPerentOptions.filter((opt) => {
      const staff = studentPerentList.find((s) => s.id === opt.value);
      const ids = parseStaffLocationIds(staff?.location_id);
      return ids.length === 0 || ids.includes(locId);
    });
  }, [studentPerentOptions, studentPerentList, formik.values.location_id]);

  const isLocationInvalid = useMemo(() => {
    if (!formik.values.location_id || staffLocationIds.length === 0) return false;
    return !staffLocationIds.includes(Number(formik.values.location_id));
  }, [formik.values.location_id, staffLocationIds]);

  // ── Conflict detection (identical shape to Create) ──
  const timeConflicts = useMemo<TimeConflict[]>(() => {
    if (!formik.values.staff_id || selectedWeekDays.length === 0) return [];
    if (!formik.values.startTime && !formik.values.endTime) return [];

    const timeToMinutes = (t: string): number => {
      if (!t) return -1;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const newStart = formik.values.startTime ? timeToMinutes(formik.values.startTime) : -1;
    const newEnd = formik.values.endTime ? timeToMinutes(formik.values.endTime) : -1;
    const hasStart = newStart >= 0;
    const hasEnd = newEnd >= 0;
    if (!hasStart && !hasEnd) return [];

    const conflicts: TimeConflict[] = [];
    staffExistingClasses.forEach((cls) => {
      const sched = cls.schedule;
      if (!sched?.startTime || !sched?.endTime || !sched?.day) return;
      if (sched.status === "deleted") return;
      if (!selectedWeekDays.includes(sched.day)) return;
      // Defense in depth — see Create/index.tsx note. Also drop the row
      // being edited itself, which otherwise self-conflicts.
      const schedStaffId = (sched as any)?.staff_id;
      if (schedStaffId != null && String(schedStaffId) !== String(formik.values.staff_id)) return;
      const schedClassId = (sched as any)?.class_id;
      if (schedClassId != null && String(schedClassId) === String((formik.values as any)?.id ?? "")) return;
      const existStart = timeToMinutes(sched.startTime);
      const existEnd = timeToMinutes(sched.endTime);
      if (existStart < 0 || existEnd < 0) return;
      let overlap = false;
      if (hasStart && hasEnd && newEnd > newStart) {
        overlap = newStart < existEnd && newEnd > existStart;
      } else if (hasStart) {
        overlap = newStart >= existStart && newStart < existEnd;
      }
      if (overlap) {
        conflicts.push({
          day: sched.day,
          startTime: sched.startTime,
          endTime: sched.endTime,
          classId: sched.class_id,
        });
      }
    });
    // dedupe
    const seen = new Set<string>();
    return conflicts.filter((c) => {
      const key = `${c.classId}-${c.day}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [formik.values.staff_id, formik.values.startTime, formik.values.endTime, selectedWeekDays, staffExistingClasses]);

  const hasConflicts = timeConflicts.length > 0;

  const timeOrderError = useMemo(() => {
    const toMin = (t: string) => {
      const [h, m] = String(t || "").split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    if (!formik.values.startTime || !formik.values.endTime) return "";
    const s = toMin(formik.values.startTime);
    const e = toMin(formik.values.endTime);
    if (e <= s) return "End time must be after start time";
    return "";
  }, [formik.values.startTime, formik.values.endTime]);

  // Dirty-state detection.
  //
  // Save Changes must stay disabled until the user actually edits a class
  // value. Formik `dirty` alone is not enough because subjects, weekdays and
  // program type live in local state (setSelectedSubject / setSelectedWeekDays
  // / setProgramType), and because the class-filter endpoint returns some
  // fields under different keys/types than formik holds them (start_time vs
  // startTime, string vs number ids, null vs default "exempt"/"regular"/today).
  //
  // We snapshot BOTH sides through the same normaliser, then compare. Any
  // field that is either editable on this page OR sent in the update payload
  // is included. `editScope` is intentionally excluded — it is a scope
  // selector for the update, not a class-data change, and toggling it must
  // not enable Save on its own (per spec).
  const snapshotFromServer = useMemo(() => {
    if (!classData) return null;
    const raw: any = classData;
    return buildEditSnapshot({
      staff_id: raw.staff_id,
      location_id: raw.location_id,
      class_mode: raw.class_mode,
      class_type: raw.class_type,
      number_of_student: raw.number_of_student,
      notes: raw.notes,
      startTime: raw.start_time ?? raw.startTime,
      endTime: raw.end_time ?? raw.endTime,
      period_from: raw.period_based_from_date,
      period_to: raw.period_based_to_date,
      default_fee: raw.default_fee,
      default_tax_status: raw.default_tax_status,
      default_fee_effective_from: raw.default_fee_effective_from,
      program_type: raw.program_type,
      subjects: raw.select_grade_subject_ids,
      // Pre-derive from availability (etc.) so the server snapshot sees
      // the same canonical set the form will after hydration. Without
      // this, snapshot-server came out as [] (no flat field) while
      // snapshot-form sat at [] too (bug) OR at the real list (after we
      // fixed hydration) — making every edit falsely look "dirty on
      // weekdays" or hiding real dirtiness.
      weekdays: deriveWeekdaysFromClassData(raw),
    });
  }, [classData]);

  const snapshotFromForm = useMemo(() => {
    return buildEditSnapshot({
      staff_id: formik.values.staff_id,
      location_id: formik.values.location_id,
      class_mode: formik.values.class_mode,
      class_type: formik.values.class_type,
      number_of_student: formik.values.number_of_student,
      notes: formik.values.notes,
      startTime: formik.values.startTime,
      endTime: formik.values.endTime,
      period_from: formik.values.period_based_from_date,
      period_to: formik.values.period_based_to_date,
      default_fee: formik.values.default_fee,
      default_tax_status: formik.values.default_tax_status,
      default_fee_effective_from: formik.values.default_fee_effective_from,
      program_type: programType,
      subjects: selectedSubject,
      weekdays: selectedWeekDays,
    });
  }, [formik.values, programType, selectedSubject, selectedWeekDays]);

  const hasChanges = useMemo(() => {
    if (!snapshotFromServer) return false;
    return snapshotFromServer !== snapshotFromForm;
  }, [snapshotFromServer, snapshotFromForm]);

  // Compute the first validation reason that would keep Save disabled so
  // we can surface it on both Save buttons (brief §7 — do not silently
  // disable Save without showing why). Order matches formValid below.
  const validationReason = useMemo((): string | null => {
    if (!formik.values.staff_id) return "Select a staff member.";
    if (!formik.values.startTime) return "Set a start time.";
    if (!formik.values.endTime) return "Set an end time.";
    if (!formik.values.class_mode) return "Choose a teaching mode.";
    if (requiresLocation(formik.values.class_mode) && !formik.values.location_id)
      return "Select a location for in-person / hybrid classes.";
    if (isLocationInvalid) return "The selected location is not valid for the staff member.";
    if (hasConflicts) return "Resolve the time conflict with another class.";
    if (selectedWeekDays.length === 0) return "Pick at least one day of the week.";
    if (timeOrderError) return "End time must be after start time.";
    if (
      assignedCount > 0 &&
      formik.values.number_of_student != null &&
      Number(formik.values.number_of_student) < assignedCount
    ) {
      return `Capacity cannot be lower than enrolled count (${assignedCount}).`;
    }
    return null;
  }, [formik.values, isLocationInvalid, hasConflicts, selectedWeekDays, timeOrderError, assignedCount]);

  const formValid = useMemo(() => {
    if (!formik.values.staff_id || !formik.values.startTime || !formik.values.endTime) return false;
    if (!formik.values.class_mode) return false;
    if (requiresLocation(formik.values.class_mode) && !formik.values.location_id) return false;
    if (isLocationInvalid) return false;
    if (hasConflicts) return false;
    if (selectedWeekDays.length === 0) return false;
    if (timeOrderError) return false;
    if (
      assignedCount > 0 &&
      formik.values.number_of_student != null &&
      Number(formik.values.number_of_student) < assignedCount
    ) {
      return false;
    }
    return true;
  }, [formik.values, isLocationInvalid, hasConflicts, selectedWeekDays, timeOrderError, assignedCount]);

  const confirm = useConfirm();

  // ── Handlers ──
  const handleClassModeChange = async (nextMode: string) => {
    // Hybrid → Online or Hybrid → In-Person is a NARROWING change: any
    // students with the mode being dropped would be silently converted. For
    // now the safe default is to require the user to confirm; a full
    // Narrowing Dialog with the affected-student list is a follow-up. We
    // simply block the narrowing until the server-side conversion story
    // lands and prompt the user in a lightweight confirm.
    const current = formik.values.class_mode;
    const narrowing =
      current === TeachMood.Hybrid &&
      (nextMode === TeachMood.Online || nextMode === TeachMood.Offline) &&
      assignedCount > 0;
    if (narrowing) {
      const ok = await confirm({
        title: "Narrow class mode?",
        message: `Changing this class from Hybrid to ${nextMode === TeachMood.Online ? "Online only" : "In-Person only"} affects the ${assignedCount} enrolled student(s). Continue?`,
        confirmText: "Change mode",
        cancelText: "Keep Hybrid",
        variant: "warning",
      });
      if (!ok) return;
    }
    formik.setFieldValue("class_mode", nextMode);
    if (nextMode === TeachMood.Online || nextMode === "") {
      formik.setFieldValue("location_id", "");
    }
  };

  const handleClassTypeChange = (v: string) => {
    formik.setFieldValue("class_type", formik.values.class_type === v ? "" : v);
  };

  const handleSubjectToggle = (sgId: number) => {
    setSelectedSubject((prev) =>
      prev.includes(sgId) ? prev.filter((x) => x !== sgId) : [...prev, sgId]
    );
  };

  const handleWeekDayToggle = (day: string) => {
    setSelectedWeekDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const handleStaffChange = (_: any, newValue: CustomerOption | null) => {
    const staffId = newValue?.value ? Number(newValue.value) : null;
    formik.setFieldValue("staff_id", staffId);
    // Editing staff resets subject selection only — days/times are kept so
    // the user isn't re-entering the whole schedule for a staff swap.
    setSelectedSubject([]);
  };

  // The full-screen loader may ONLY block for the one thing that cannot be
  // deferred: the class record itself (Formik needs it to seed initial
  // values). Static lookups — locations, staff/team, subjects, staff-grade
  // pairings — load in parallel and populate their own dropdowns as they
  // arrive. The previous gate blocked the whole route on all five, so even
  // when the slim class-detail query had already resolved the user still
  // stared at the blurred Step Up overlay while a stale staff list crawled
  // in on the single-threaded dev server.
  const pageLoading = classIsLoading && !classData;

  if (pageLoading || !classData) {
    return <LoadingPage open={pageLoading || !classData} />;
  }

  const recurring = classData.class_type === ClassType["Regular"];

  // Effective date: the date from which future occurrences will be regenerated.
  // If we came in through a specific occurrence, use its scheduled date; else
  // "today". Displayed in the context band so the user knows what "future" means.
  const effectiveDate = (occurrence?.scheduledDate as string) || dayjs().format("YYYY-MM-DD");
  const occurrenceDate = occurrence?.scheduledDate
    ? dayjs(occurrence.scheduledDate as string).format("ddd, MMM D, YYYY")
    : null;

  const contextBand = recurring ? (
    <Box
      sx={{
        borderRadius: "12px",
        border: `1px solid ${alpha("#6366f1", 0.2)}`,
        bgcolor: alpha("#6366f1", 0.04),
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }} alignItems={{ sm: "center" }}>
        <Chip
          icon={<EventRepeatOutlinedIcon sx={{ fontSize: "16px !important" }} />}
          label="Editing recurring class schedule"
          size="small"
          sx={{
            height: 26, fontWeight: 600, fontSize: 12,
            bgcolor: alpha("#6366f1", 0.12), color: "#4f46e5",
          }}
        />
        {occurrenceDate && (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            Selected occurrence:{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
              {occurrenceDate}
            </Box>
          </Typography>
        )}
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
          Changes affect:{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
            Future occurrences from {dayjs(effectiveDate).format("MMM D, YYYY")}
          </Box>
        </Typography>
      </Stack>
    </Box>
  ) : null;

  // Reset restores server values (does NOT clear the form).
  const handleReset = () => {
    if (!classData) return;
    formik.resetForm({
      values: {
        id: classId,
        staff_id: (classData?.staff_id as any) ?? null,
        location_id: (classData?.location_id as any) ?? null,
        class_mode: normalizeTeachMode(classData?.class_mode),
        class_type: classData?.class_type ?? "",
        number_of_student: classData?.number_of_student ?? null,
        select_grade_subject_ids: [],
        period_based_from_date: (classData as any)?.period_based_from_date
          ? dayjs((classData as any).period_based_from_date)
          : null,
        period_based_to_date: (classData as any)?.period_based_to_date
          ? dayjs((classData as any).period_based_to_date)
          : null,
        notes: (classData as any)?.notes ?? "",
        selected_days: [],
        // Match initialValues — snake_case wins, camelCase is the fallback.
        startTime: (classData as any)?.start_time ?? classData?.startTime ?? "",
        endTime: (classData as any)?.end_time ?? classData?.endTime ?? "",
      },
    });
    setSelectedSubject((classData.select_grade_subject_ids as number[]) ?? []);
    // Same derive path as the initial seed effect so Reset reproduces the
    // exact selected-day set the page loaded with, regardless of whether
    // the API returned flat `selected_days` or the nested `availability`.
    setSelectedWeekDays(deriveWeekdaysFromClassData(classData));
  };

  return (
    <Box sx={{ pb: 4 }}>
      <ClassForm
        mode="edit"
        formik={formik}
        formValid={formValid}
        submitting={isUpdating}
        hasChanges={hasChanges}
        validationReason={validationReason}
        studentPerentOptions={studentPerentOptions}
        filteredStaffOptions={filteredStaffOptions}
        studentPerentIsLoading={studentPerentIsLoading}
        onStaffChange={handleStaffChange}
        onStaffSearchInput={(v) => setCurrentSearchQuery(v)}
        locationOptions={locationOptions}
        filteredLocationOptions={filteredLocationOptions}
        staffLocationIds={staffLocationIds}
        isLocationInvalid={isLocationInvalid}
        locationIsLoading={locationIsLoading}
        onClassModeChange={handleClassModeChange}
        onClassTypeChange={handleClassTypeChange}
        programType={programType}
        onProgramTypeChange={setProgramType}
        subjectGRadeDataList={subjectGRadeDataList}
        subjectMap={subjectMap}
        subjectIsLoading={subjectIsLoading}
        subjectGradeIsLoading={subjectGradeIsLoading}
        isSubjectError={!!isError}
        selectedSubject={selectedSubject}
        onSubjectToggle={handleSubjectToggle}
        onAddSubjectAssignments={handleAddSubjectAssignments}
        protectedSubjectIds={protectedSubjectIds}
        protectedSubjectMessage="This subject cannot be removed because classes or student assignments already exist for it."
        selectedWeekDays={selectedWeekDays}
        onWeekDayToggle={handleWeekDayToggle}
        timeConflicts={timeConflicts}
        hasConflicts={hasConflicts}
        timeOrderError={timeOrderError}
        assignedCount={assignedCount}
        editScope={editScope}
        onEditScopeChange={setEditScope}
        showEditScopeBand={recurring}
        currentClassFeeFromServer={(classData as any)?.default_fee != null ? Number((classData as any).default_fee) : null}
        submitLabel="Save Changes"
        onReset={handleReset}
        onCancel={() => navigate(returnUrl)}
        showCancel
        contextBand={contextBand}
      />
      {isUpdating && <BackDrop open={isUpdating} />}
    </Box>
  );
};

export default Index;
